import { create } from 'zustand';
import {
  FormStructure,
  FormField,
  FieldType,
  FormPage as Page,
  FormSection as Section,
} from '@/features/application/types/forms';
import { initialFormStructure } from '../constants/initial-data';
import { logger } from '@/features/help/utils/logger';
import { toast } from 'react-hot-toast';
import { useCrmStore } from './crm.store';
import { useMappingsStore } from './mappings.store';

// For UI selection of CRM objects
export interface CrmObjectOption {
  id: string; // e.g., "Account", "MyCustomObject__c"
  label: string; // e.g., "Account", "My Custom Object"
}

export interface FormBuilderStore {
  activeFieldId: string | null;
  expandedPageId: string | null; // Add this to track which page is expanded
  isEditable: boolean;
  isLoading: boolean;
  error: string | null;
  dashboardInitialized: boolean;
  setDashboardInitialized: (initialized: boolean) => void;
  currentMappingStructure: FormStructure | null;
  supportedCrmObjects: CrmObjectOption[];
  setActiveFieldId: (fieldId: string | null) => void;
  setExpandedPageId: (pageId: string | null) => void; // Add this action
  loadStructureForCurrentMapping: () => void;
  updateFieldInCurrentMapping: (
    fieldId: string,
    updates: Partial<
      Pick<
        FormField,
        | 'path'
        | 'fieldApiName'
        | 'fieldLabel'
        | 'crmFieldType'
        | 'crmPrecision'
        | 'crmScale'
        | 'crmPicklistValues'
        | 'crmMaxLength'
        | 'formFieldMaxLength'
        | 'type'
        | 'originalPicklistOptions'
        | 'currentPicklistOptions'
        | 'originalType'
        | 'placeholder'
        | 'isPathConfirmed'
      >
    >
  ) => void;
  addFieldToCurrentMapping: (sectionId: string, fieldType: FieldType) => void;
  toggleFieldRequiredInCurrentMapping: (fieldId: string) => void;
  clearFieldMappingInCurrentStructure: (fieldId: string) => void;
  deleteFieldInCurrentMapping: (fieldId: string) => void;
  moveFieldInCurrentMapping: (
    _sourceId: string,
    _targetId: string,
    _sourceSectionId: string,
    _targetSectionId: string
  ) => void;
  deleteSectionInCurrentMapping: (sectionId: string) => void;
  moveSectionInCurrentMapping: (sourceId: string, targetId: string) => void;
  getAllowedObjectsForCurrentMapping: () => Set<string> | null;
  moveToNextUnmappedField: (currentFieldId: string) => void;
}

export const useFormBuilderStore = create<FormBuilderStore>((set, get) => ({
  activeFieldId: null,
  expandedPageId: null, // Initialize the new state
  isEditable: true,
  isLoading: false,
  error: null,
  dashboardInitialized: false,
  setDashboardInitialized: (initialized) => set({ dashboardInitialized: initialized }),
  currentMappingStructure: null,
  supportedCrmObjects: [],
  setActiveFieldId: (fieldId) => {
    // When setting an active field, also expand its parent page
    if (fieldId) {
      const currentStructure = get().currentMappingStructure;
      if (currentStructure) {
        // Find which page contains this field
        const parentPage = currentStructure.pages.find((page) =>
          page.sections.some((section) => section.fields.some((field) => field.id === fieldId))
        );
        if (parentPage) {
          set({ activeFieldId: fieldId, expandedPageId: parentPage.id });
          return;
        }
      }
    }
    set({ activeFieldId: fieldId });
  },
  setExpandedPageId: (pageId) => {
    // When expanding a page, close any active field if it's not in this page
    const currentState = get();
    if (pageId && currentState.activeFieldId && currentState.currentMappingStructure) {
      const targetPage = currentState.currentMappingStructure.pages.find((p) => p.id === pageId);
      const activeFieldInTargetPage = targetPage?.sections.some((section) =>
        section.fields.some((field) => field.id === currentState.activeFieldId)
      );

      if (!activeFieldInTargetPage) {
        set({ expandedPageId: pageId, activeFieldId: null });
        return;
      }
    }
    set({ expandedPageId: pageId });
  },

  loadStructureForCurrentMapping: () => {
    const { currentFieldMappingId, mappingDetails } = useMappingsStore.getState();

    if (!currentFieldMappingId) {
      set({ currentMappingStructure: initialFormStructure });
      return;
    }

    const details = mappingDetails.find((d) => d.mapping_id === currentFieldMappingId);

    if (details) {
      set({
        currentMappingStructure: details.form_structure,
      });
    } else {
      // Fallback to initial structure if details for some reason aren't found
      set({ currentMappingStructure: initialFormStructure });
      logger.warn(
        'FormBuilderStore',
        `Could not find details for mapping ID ${currentFieldMappingId} in cache.`,
        {
          totalDetailsCached: mappingDetails.length,
        }
      );
    }
  },

  updateFieldInCurrentMapping: (
    fieldId: string,
    updates: Partial<
      Pick<
        FormField,
        | 'path'
        | 'fieldApiName'
        | 'fieldLabel'
        | 'crmFieldType'
        | 'crmPrecision'
        | 'crmScale'
        | 'crmPicklistValues'
        | 'crmMaxLength'
        | 'formFieldMaxLength'
        | 'type'
        | 'originalPicklistOptions'
        | 'currentPicklistOptions'
        | 'originalType'
        | 'placeholder'
        | 'isPathConfirmed'
      >
    >
  ) => {
    const currentStructure = get().currentMappingStructure;
    if (!currentStructure?.pages) return;

    const updatedPages = currentStructure.pages.map((page: Page) => ({
      ...page,
      sections: page.sections.map((section: Section) => ({
        ...section,
        fields: section.fields.map((field: FormField) => {
          if (field.id === fieldId) {
            return { ...field, ...updates };
          }
          return field;
        }),
      })),
    }));

    set({ currentMappingStructure: { ...currentStructure, pages: updatedPages } });
  },

  addFieldToCurrentMapping: (sectionId: string, fieldType: FieldType) => {
    const currentStructure = get().currentMappingStructure;
    if (!currentStructure?.pages) return;
    const newField: FormField = {
      id: `field_${new Date().getTime()}`,
      type: fieldType,
      label: `New ${fieldType} field`,
      value: null,
      displayName: `New ${fieldType} field`,
      required: false,
      path: [], // Initialize with empty path
      permissions: { canEdit: [1], canView: [1] },
      ...(fieldType === 'file' && { acceptedFileTypes: ['.pdf', '.jpg', '.png'] }),
    };
    const updatedPages = currentStructure.pages.map((page: Page) => ({
      ...page,
      sections: page.sections.map((section: Section) =>
        section.id === sectionId ? { ...section, fields: [...section.fields, newField] } : section
      ),
    }));
    set({
      currentMappingStructure: { ...currentStructure, pages: updatedPages },
      activeFieldId: newField.id,
    });
  },

  deleteFieldInCurrentMapping: (fieldId: string) => {
    const currentStructure = get().currentMappingStructure;
    if (!currentStructure?.pages) return;
    logger.debug('FormBuilderStore', 'Deleting field from current mapping:', { fieldId });
    const updatedPages = currentStructure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        fields: section.fields.filter((field) => field.id !== fieldId),
      })),
    }));
    const finalPages = updatedPages.map((page) => ({
      ...page,
      sections: page.sections.filter((section) => section.fields.length > 0),
    }));
    set({ currentMappingStructure: { ...currentStructure, pages: finalPages } });
  },

  moveFieldInCurrentMapping: () => {
    logger.info('FormBuilderStore', 'moveFieldInCurrentMapping not yet implemented');
  },

  toggleFieldRequiredInCurrentMapping: (fieldId: string) => {
    const currentStructure = get().currentMappingStructure;
    if (!currentStructure?.pages) return;
    let fieldUpdated = false;
    const updatedPages = currentStructure.pages.map((page: Page) => ({
      ...page,
      sections: page.sections.map((section: Section) => ({
        ...section,
        fields: section.fields.map((field: FormField) => {
          if (field.id === fieldId) {
            fieldUpdated = true;
            return { ...field, required: !field.required };
          }
          return field;
        }),
      })),
    }));
    if (fieldUpdated) {
      set({ currentMappingStructure: { ...currentStructure, pages: updatedPages } });
    }
  },

  clearFieldMappingInCurrentStructure: (fieldId: string) => {
    const currentStructure = get().currentMappingStructure;
    if (!currentStructure?.pages) return;
    const updatedPages = currentStructure.pages.map((page: Page) => ({
      ...page,
      sections: page.sections.map((section: Section) => ({
        ...section,
        fields: section.fields.map((field: FormField) => {
          if (field.id === fieldId) {
            const clearedField = {
              ...field,
              value: null,
              path: [],
              fieldApiName: undefined,
              fieldLabel: undefined,
              crmFieldType: undefined,
              crmPrecision: undefined,
              crmPicklistValues: undefined,
              crmMaxLength: undefined,
              formFieldMaxLength: undefined,
              originalPicklistOptions: undefined,
              currentPicklistOptions: undefined,
              isPathConfirmed: undefined,
            };

            if (field.type === 'picklist' && field.originalType) {
              clearedField.type = field.originalType;
              clearedField.originalType = undefined;
            }

            return clearedField;
          }
          return field;
        }),
      })),
    }));
    set({ currentMappingStructure: { ...currentStructure, pages: updatedPages } });
  },

  deleteSectionInCurrentMapping: (sectionId: string) => {
    const currentStructure = get().currentMappingStructure;
    if (!currentStructure) return;
    const newPages = currentStructure.pages.map((page: Page) => ({
      ...page,
      sections: page.sections.filter((section: Section) => section.id !== sectionId),
    }));
    set({ currentMappingStructure: { ...currentStructure, pages: newPages } });
    toast.success('Section deleted successfully');
  },

  moveSectionInCurrentMapping: () => {
    logger.info('FormBuilderStore', 'moveSectionInCurrentMapping not yet implemented');
  },

  getAllowedObjectsForCurrentMapping: () => {
    const { getCurrentPrimaryObject } = useMappingsStore.getState();
    const primaryObject = getCurrentPrimaryObject();
    if (!primaryObject) return null;

    return useCrmStore.getState().getAllowedObjectsForPrimaryObject(primaryObject);
  },
  moveToNextUnmappedField: (currentFieldId) => {
    const { currentMappingStructure } = get();
    if (!currentMappingStructure) return;

    const allFields: FormField[] = [];
    currentMappingStructure.pages.forEach((page) =>
      page.sections.forEach((section) => allFields.push(...section.fields))
    );

    const currentIndex = allFields.findIndex((f) => f.id === currentFieldId);

    if (currentIndex === -1) return;

    // Find the next unmapped field after the current one
    for (let i = currentIndex + 1; i < allFields.length; i++) {
      const field = allFields[i];
      if (!field.fieldApiName) {
        set({ activeFieldId: field.id });
        return;
      }
    }

    // If no unmapped field is found after, search from the beginning
    for (let i = 0; i < currentIndex; i++) {
      const field = allFields[i];
      if (!field.fieldApiName) {
        set({ activeFieldId: field.id });
        return;
      }
    }

    // If all fields are mapped, close the current one
    set({ activeFieldId: null });
    toast.success('All fields have been mapped!');
  },
}));
